import type { RewriteOptions, RewriteTier, Rewrites, Usage } from '@lyrmo/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/api-error';
import type { RewriteClient } from '../../api/client';
import { useCooldown } from '../../hooks/useCooldown';
import { withViewTransition } from '../../lib/motion';
import type { Passage } from './passage';

// Used when the server rate limits a request without saying how long to wait.
const DEFAULT_RETRY_AFTER_SECONDS = 30;

/** A request: the text sent, and when it's part of the draft, which part. */
interface Request {
  readonly id: number;
  readonly text: string;
  readonly passage?: Passage | undefined;
}

export type RewriteState =
  | { readonly status: 'idle' }
  | (Request & { readonly status: 'loading' })
  | (Request & {
      readonly status: 'success';
      readonly rewrites: Rewrites;
      /** The versions as the rewrite returned them, once other words are swapped into any. */
      readonly returned?: Rewrites;
    })
  | (Request & { readonly status: 'error'; readonly error: ApiError });

interface UseRewriteOptions {
  /** Receives the day's word usage, which every rewrite response reports. */
  readonly onUsage?: ((usage: Usage) => void) | undefined;
  /** Versions of `text` made already, to start with, rather than rewrite it again. */
  readonly initial?: { readonly text: string; readonly rewrites: Rewrites } | undefined;
}

/**
 * Runs rewrite requests. Starting a new request or resetting aborts the one in flight, which
 * also cancels the model call on the server. A rate-limited response starts a cooldown.
 */
export function useRewrite(client: RewriteClient, { onUsage, initial }: UseRewriteOptions = {}) {
  const [state, setState] = useState<RewriteState>(() =>
    initial
      ? { status: 'success', id: 1, text: initial.text, rewrites: initial.rewrites }
      : { status: 'idle' },
  );
  // Each change of state reshapes the versions: they grow or shrink to it, rather than jump.
  const show = useCallback((next: RewriteState) => {
    withViewTransition('results', () => {
      setState(next);
    });
  }, []);
  const { secondsLeft: cooldownSeconds, start: startCooldown } = useCooldown();
  const inFlight = useRef<{ id: number; controller: AbortController } | null>(null);
  const lastId = useRef(initial ? 1 : 0);

  const cancel = useCallback(() => {
    inFlight.current?.controller.abort();
    inFlight.current = null;
  }, []);

  /** Rewrites `text`: all of the draft, or with `passage`, that part of it alone. */
  const submit = useCallback(
    async (text: string, options?: RewriteOptions, passage?: Passage) => {
      cancel();
      const id = ++lastId.current;
      const controller = new AbortController();
      inFlight.current = { id, controller };
      // A reset or a newer submit may replace this request while it's in flight.
      const isCurrent = () => inFlight.current?.id === id;
      show({ status: 'loading', id, text, passage });

      try {
        const { rewrites, usage } = await client.createRewrite(text, {
          signal: controller.signal,
          options,
          ...(passage && { context: passage.context }),
        });
        onUsage?.(usage);
        if (isCurrent()) show({ status: 'success', id, text, passage, rewrites });
      } catch (error) {
        if (error instanceof ApiError && error.usage) onUsage?.(error.usage);
        if (!isCurrent()) return;
        const apiError =
          error instanceof ApiError
            ? error
            : new ApiError('INTERNAL_ERROR', 'Something went wrong. Please try again.', {
                cause: error,
              });
        if (apiError.code === 'RATE_LIMITED') {
          startCooldown(apiError.retryAfter ?? DEFAULT_RETRY_AFTER_SECONDS);
        }
        show({ status: 'error', id, text, passage, error: apiError });
      } finally {
        if (isCurrent()) inFlight.current = null;
      }
    },
    [cancel, client, onUsage, show, startCooldown],
  );

  /**
   * Makes `text` the version of `tier` from request `id`, as when other words are swapped in for
   * its changes. Copying, using, hearing, and saving the version then take it as it now reads.
   */
  const revise = useCallback((id: number, tier: RewriteTier, text: string) => {
    setState((current) =>
      current.status === 'success' && current.id === id
        ? {
            ...current,
            returned: current.returned ?? current.rewrites,
            rewrites: { ...current.rewrites, [tier]: text },
          }
        : current,
    );
  }, []);

  const reset = useCallback(() => {
    cancel();
    show({ status: 'idle' });
  }, [cancel, show]);

  useEffect(() => cancel, [cancel]);

  return { state, cooldownSeconds, submit, revise, reset };
}
